/**
 * Created by Jie Gao on 2018-11-02.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {IWinRatioConfig} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {CoinsFountain} from "../../coinfountain/CoinsFountain";
import {DragonTribeGameConfig} from "../../DragonTribeGameConfig";

export class DragonTribeBigWinView extends BaseView {
    private TEXTS:string[] = ["Big Win", "Mega Win", "Super Mega Win"];
    private _layer:ScalableLayer;
    private _isShowing:boolean = false;
    private _textContainer:PIXI.Sprite;
    private _dimmer:PIXI.Graphics;
    private _starLayerTop:PIXI.Sprite;
    private _starLayerBottom:PIXI.Sprite;
    private _texts:PIXI.Sprite[];
    private _coinsFountain:CoinsFountain;
    private _currentTimeLine:TimelineLite | null;

    constructor() {
        super();
        this._layer = StageManager.getLayer(DragonTribeGameConfig.instance.LAYERS.BIG_WIN.name);
        this._dimmer = new PIXI.Graphics();
        this._dimmer.alpha = 0;
        this._layer.addChild(this._dimmer);
        this._coinsFountain = new CoinsFountain(this._layer);
    }

    protected initAnimations():void {
        this._starLayerTop = new PIXI.Sprite(GameResources.getTextures("bigWinBgStar")[0]);
        this._starLayerTop.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._starLayerTop.scale.set(1.5, 1.5);
        this._starLayerTop.alpha = 0;
        this._starLayerTop.anchor.set(0.5, 0.5);
        this._starLayerTop.position.set(DragonTribeGameConfig.instance.GAME_WIDTH * 0.5, DragonTribeGameConfig.instance.GAME_HEIGHT * 0.5);
        this._starLayerBottom = new PIXI.Sprite(GameResources.getTextures("bigWinBgStar")[0]);
        this._starLayerBottom.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._starLayerBottom.alpha = 0;
        this._starLayerBottom.scale.set(1, 1);
        this._starLayerBottom.anchor.set(0.5, 0.5);
        this._starLayerBottom.position.set(DragonTribeGameConfig.instance.GAME_WIDTH * 0.5, DragonTribeGameConfig.instance.GAME_HEIGHT * 0.5);
        this._textContainer = new PIXI.Sprite();
        this._textContainer.anchor.set(0.5, 0.5);
        this._textContainer.position.set(DragonTribeGameConfig.instance.GAME_WIDTH * 0.5, DragonTribeGameConfig.instance.GAME_HEIGHT * 0.24);

        this._layer.addChild(this._starLayerBottom);
        this._layer.addChild(this._starLayerTop);
        this._layer.addChild(this._textContainer);

        this._texts = ArrayHelper.initArrayWithValues(this.TEXTS.length, (i:number) => {
            const text:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("bigWinText" + i)[0]);
            text.y = -text.height / 2;
            text.x = -text.width / 2;
            text.alpha = 0;
            this._textContainer.addChild(text);
            return text;
        });
    }

    protected onResize(resizeData:IResizeData):void {
        if(!this._isShowing) {
            this._isResizeDirty = true;
            return;
        }

        const screenWidth:number = Math.ceil(resizeData.width / this._layer.scale.x);
        const screenHeight:number = Math.ceil(resizeData.height / this._layer.scale.y);
        const leftOffset:number = Math.floor(-this._layer.x / this._layer.scale.x);
        const topOffset:number = Math.floor(-this._layer.y / this._layer.scale.y);

        this._dimmer.clear();
        this._dimmer.beginFill(0x000000, 0.6);
        this._dimmer.drawRect(leftOffset, topOffset, screenWidth, screenHeight);

        this._isResizeDirty = false;
    }

    public startWinPresentation():void {
        this._currentTimeLine = new TimelineLite();
        this._currentTimeLine.to(this._starLayerTop, 300, {rotation : -360, repeat : 3, repeatDelay : 0.5}, 0);
        this._currentTimeLine.to(this._starLayerBottom, 300, {rotation : 360, repeat : 3, repeatDelay : 0.5}, 0);
    }

    public getBigWinLevelAnimation(level:number, winRatioConfig:IWinRatioConfig):Timeline {
        this._isShowing = true;
        if(this._isResizeDirty) {
            this.onResize(this._resizeData);
        }

        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => this._coinsFountain.startFountain(level),
            this.updateBigWinLevelAnimation(level),
            TweenLite.to(this._dimmer, 0.1, {alpha : 0.8}),
            TweenLite.to(this._starLayerTop, 0.1, {alpha : 0.6}),
            TweenLite.to(this._starLayerBottom, 0.1, {alpha : 1}),
            TweenLite.to(this, winRatioConfig.duration, {alpha : 1})
        ], 0);
        return timeline;
    }

    private updateBigWinLevelAnimation(level:number):TimelineLite {
        const timeline:TimelineLite = new TimelineLite();
        this._texts.forEach((text:PIXI.Sprite, i:number) => {
            text.alpha = (i === level) ? 1 : 0;
        });

        timeline.add([
            TweenLite.fromTo(this._textContainer, 7 * 0.033, {alpha : 0}, {alpha : 1}),
            TweenLite.fromTo(this._textContainer.scale, 7 * 0.033, {x : 2, y : 2}, {x : 1, y : 1})
        ], 0);

        return timeline;
    }

    public playAbortedEndingAnimation(level:number):void {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._texts.forEach((text:PIXI.Sprite, i:number) => {
                    text.alpha = (i === level) ? 1 : 0;
                });
                this._textContainer.alpha = 1;
                this._textContainer.scale.set(1);
                this._coinsFountain.stopFountain();
                Logger.logDev(`big win view playAbortedEndingAnimation`);
            },
            TweenLite.to(this._dimmer, 1, {alpha : 0}),
            TweenLite.to(this._starLayerTop, 1, {alpha : 0}),
            TweenLite.to(this._starLayerBottom, 1, {alpha : 0}),
            TweenLite.to(this._texts[level], 1.5, {alpha : 0})
        ]);
        timeline.add(() => {
            this._isShowing = false;
            this.reset();
        });

        timeline.play();
    }

    private reset():void {
        this._textContainer.scale.set(1, 1);
        this._texts.forEach((text:PIXI.Sprite) => {
            text.alpha = 0;
        });
        if(this._currentTimeLine && this._currentTimeLine.isActive()) {
            this._currentTimeLine.pause();
            this._currentTimeLine.kill();
        }
        this._currentTimeLine = null;
    }
}